import type { MsrDatenpunktZeile, MsrFeldgeraetDetail } from './typen';
import { ART, HERKUNFT_FARBE, herkunftMarke, z } from './gemeinsam';
import {
  ContentSection, DataTable, EmptyState, LoadingState, PropertyGrid, StatusBadge,
  type Spalte,
} from '../../muster';

/**
 * Die Datenpunkttabelle eines Aggregats (Feldgerät) — VDI-3814-Spalten,
 * geschätzte Zeilen `datenfeld` (gelb), ohne AKS kursiv „— nicht vergeben —"
 * (Vertrag: `msr_datenpunkte.Aks` wird nie geändert, aber erst mit
 * `--schreiben` vergeben — bis dahin ist die Zeile trotzdem vollständig).
 */
export function DatenpunkteTabelle({ zeilen }: { zeilen: MsrDatenpunktZeile[] }) {
  const spalten: Spalte<MsrDatenpunktZeile>[] = [
    {
      schluessel: 'aks', titel: 'AKS', immerSichtbar: true, breite: '230px',
      wert: (d) => d.aks ?? null,
      inhalt: (d) => (d.aks
        ? <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{d.aks}</span>
        : <em className="schwach">— nicht vergeben —</em>),
    },
    { schluessel: 'funktion', titel: 'Funktion', wert: (d) => d.funktion },
    { schluessel: 'bezeichnung', titel: 'Bezeichnung', wert: (d) => d.bezeichnung },
    { schluessel: 'art', titel: 'Art', wert: (d) => ART[d.art] },
    { schluessel: 'signal', titel: 'Signal', wert: (d) => d.signal ?? null },
    { schluessel: 'anbindung', titel: 'Anbindung', wert: (d) => d.anbindung },
    { schluessel: 'klemmen', titel: 'Klemmen', art: 'zahl', wert: (d) => d.klemmen },
    {
      schluessel: 'herkunft', titel: 'Herkunft', art: 'status', breite: '110px',
      wert: (d) => HERKUNFT_FARBE[d.herkunft].text,
      inhalt: (d) => <StatusBadge {...herkunftMarke(d.herkunft)} klein />,
    },
  ];

  return (
    <DataTable
      name="Datenpunkte"
      spalten={spalten}
      zeilen={zeilen}
      schluessel={(d) => d.id}
      dicht
      ausfuhr
      ausfuhrDateiname="msr-datenpunkte-aggregat"
      zeilenKlasse={(d) => (d.herkunft === 'geschaetzt' ? 'datenfeld' : undefined)}
      leer={(
        <EmptyState
          klein
          titel="Für dieses Aggregat ist kein Datenpunkt abgeleitet."
          erklaerung="Ohne passende Datenpunktvorlage entsteht kein Datenpunkt."
        />
      )}
    />
  );
}

/**
 * Das Feldgerät (Aggregat) im Detail — Baum-Reiter, Auswahl „Aggregat".
 */
export function AggregatDetail({ feldgeraet }: {
  feldgeraet: MsrFeldgeraetDetail | null;
}) {
  if (!feldgeraet) return <LoadingState text="Aggregat wird abgerufen …" zeilen={5} spalten={3} />;

  const bedingungen = Object.entries(feldgeraet.bedingungen ?? {});

  return (
    <>
      <ContentSection
        titel={feldgeraet.aggregat}
        beschreibung={feldgeraet.bezeichnung ?? undefined}
        meta={`Quelle ${feldgeraet.quelleTabelle}`}
      >
        <PropertyGrid
          spalten={3}
          eintraege={[
            { beschriftung: 'Bauteilart', wert: feldgeraet.bauteilart, herkunft: 'modell' },
            { beschriftung: 'Familie', wert: feldgeraet.familie ?? null, herkunft: 'modell' },
            { beschriftung: 'Typ', wert: feldgeraet.typ ?? null, herkunft: 'modell' },
            {
              beschriftung: 'Fabrikat',
              wert: (feldgeraet.hersteller ?? '—')
                + (feldgeraet.modell ? ` · ${feldgeraet.modell}` : ''),
              herkunft: feldgeraet.fabrikatGepflegt ? 'modell' : 'daten',
              hinweis: feldgeraet.fabrikatGepflegt
                ? undefined : 'Regelfall angenommen — kein gepflegtes Fabrikat.',
            },
            { beschriftung: 'Anbindung', wert: feldgeraet.anbindung },
            { beschriftung: 'Geschoss', wert: feldgeraet.geschoss ?? null },
            { beschriftung: 'Raum', wert: feldgeraet.raumnummer ?? null },
            { beschriftung: 'Anlage', wert: feldgeraet.anlageKennung ?? null },
            { beschriftung: 'ASP', wert: feldgeraet.aspKennung ?? null },
            ...(feldgeraet.leitungslaengeM !== undefined ? [{
              beschriftung: 'Leitungslänge', wert: feldgeraet.leitungslaengeM, einheit: 'm',
            }] : []),
            ...(bedingungen.length > 0 ? [{
              beschriftung: 'Bedingungen', breit: true,
              wert: bedingungen.map(([k, v]) => `${k}=${v ? 'ja' : 'nein'}`).join(', '),
            }] : []),
          ]}
        />
      </ContentSection>

      <ContentSection titel="Datenpunkte" meta={`${z(feldgeraet.datenpunkte.length)} Zeilen`}>
        <DatenpunkteTabelle zeilen={feldgeraet.datenpunkte} />
      </ContentSection>
    </>
  );
}
